import { ChevronDown } from 'lucide-react';
import { useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

export type NavLinkItem = {
	type: 'link';
	to: string;
	label: string;
	scope?: string | string[];
};

export type NavSubItem = {
	to: string;
	label: string;
	scope?: string | string[];
};

export type NavMenuGroup = {
	type: 'menu';
	label: string;
	items: NavSubItem[];
	scope?: string | string[];
};

export type NavEntry = NavLinkItem | NavMenuGroup;

function NavItemLink({
	to,
	label,
	end,
}: {
	to: string;
	label: string;
	end?: boolean;
}) {
	return (
		<NavLink
			to={to}
			end={end}
			className={({ isActive }) =>
				cn(
					'text-xl transition-colors hover:text-foreground',
					isActive ? 'font-medium text-foreground' : 'text-muted-foreground',
				)
			}
		>
			{label}
		</NavLink>
	);
}

function NavMenuTrigger({ entry }: { entry: NavMenuGroup }) {
	const location = useLocation();
	const navigate = useNavigate();
	const [open, setOpen] = useState(false);
	const isActive = entry.items.some((item) => location.pathname === item.to);

	return (
		<DropdownMenu open={open} onOpenChange={setOpen}>
			<DropdownMenuTrigger
				render={
					<button
						type="button"
						className={cn(
							'flex items-center gap-1 text-xl transition-colors hover:text-foreground focus:outline-none',
							isActive
								? 'font-medium text-foreground'
								: 'text-muted-foreground',
						)}
					/>
				}
			>
				{entry.label}
				<ChevronDown
					className={cn(
						'h-5 w-5 transition-transform duration-150',
						open && 'rotate-180',
					)}
				/>
			</DropdownMenuTrigger>

			<DropdownMenuContent align="start" sideOffset={8}>
				{entry.items.map((item) => (
					<DropdownMenuItem
						key={item.to}
						className={cn(
							location.pathname === item.to && 'font-medium text-foreground',
						)}
						onClick={() => navigate(item.to)}
					>
						{item.label}
					</DropdownMenuItem>
				))}
			</DropdownMenuContent>
		</DropdownMenu>
	);
}

interface NavBarProps {
	entries: NavEntry[];
}

export function NavBar({ entries }: NavBarProps) {
	return (
		<nav className="border-b bg-card">
			<div className="flex h-24 items-center gap-8 px-8">
				{entries.map((entry) =>
					entry.type === 'link' ? (
						<NavItemLink
							key={entry.to}
							to={entry.to}
							label={entry.label}
							end={entry.to === '/'}
						/>
					) : (
						<NavMenuTrigger key={entry.label} entry={entry} />
					),
				)}
			</div>
		</nav>
	);
}
